import type { TowerDriverDetail, TowerListedShipment, TowerShipment } from "../../../types/controlTower";
import { searchKey } from "./towerFormat";
import { hasCoordinates } from "./towerShared";

// "Ubicar choferes": quién hizo un lugar en el día y quién estuvo cerca de un
// envío. Solo sugiere; no asigna ni cambia nada.

export type PlaceMatch = {
  driver: TowerDriverDetail;
  shipments: TowerShipment[];
  delivered: number;
  inTransit: number;
  lastVisit: string | null;
};

export type NearbyDriver = {
  driver: TowerDriverDetail;
  nearestKm: number | null;
  sameLocality: number;
  isCurrentDriver: boolean;
};

// Radio de sugerencia, igual que la Torre original: más lejos, no se sugiere.
export const NEARBY_KM = 8;
const ZONES = ["CABA", "NORTE", "SUR", "OESTE"];

export function distanceKm(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const rad = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// Choferes que hicieron un lugar: la búsqueda es una zona ("Oeste", "Zona
// Oeste") o parte del nombre de una localidad ("San Martin").
export function driversForPlace(drivers: TowerDriverDetail[], query: string): PlaceMatch[] {
  const q = searchKey(query).replace(/^ZONA /, "");
  if (q.length < 3 && !ZONES.includes(q)) return [];
  const isZone = ZONES.includes(q);
  const matches: PlaceMatch[] = [];
  for (const driver of drivers) {
    if (driver.kind !== "driver") continue;
    const shipments = driver.shipments.filter((s) => (isZone ? s.zone === q : searchKey(s.locality).includes(q)));
    if (!shipments.length) continue;
    const lastVisit =
      shipments
        .filter((s) => s.status === "delivered" && s.stateDate)
        .map((s) => s.stateDate as string)
        .sort()
        .pop() || null;
    matches.push({
      driver,
      shipments,
      delivered: shipments.filter((s) => s.status === "delivered").length,
      inTransit: shipments.filter((s) => s.status === "inTransit").length,
      lastVisit,
    });
  }
  return matches.sort(
    (a, b) => b.shipments.length - a.shipments.length || a.driver.name.localeCompare(b.driver.name, "es"),
  );
}

// Envíos del día por tracking. No por calle: una calle "San Martín" no dice
// nada del lugar; el lugar lo da la localidad (driversForPlace).
export function findShipments(candidates: TowerListedShipment[], query: string, limit = 10): TowerListedShipment[] {
  const q = searchKey(query).replace(/ /g, "");
  if (q.length < 4) return [];
  return candidates.filter((s) => searchKey(s.tracking).replace(/ /g, "").includes(q)).slice(0, limit);
}

// Todos los envíos del día con a quién están asignados, para buscarlos.
export function listedShipments(
  drivers: TowerDriverDetail[],
  depot: TowerListedShipment[] = [],
): TowerListedShipment[] {
  return [...drivers.flatMap((d) => d.shipments.map((s) => ({ ...s, assignedTo: d.name, driverId: d.id }))), ...depot];
}

// Choferes que estuvieron cerca de un envío: con envíos en la misma localidad
// o a NEARBY_KM o menos. Primero los más cercanos.
export function driversNear(drivers: TowerDriverDetail[], target: TowerListedShipment, limit = 8): NearbyDriver[] {
  const locality = searchKey(target.locality);
  const result: NearbyDriver[] = [];
  for (const driver of drivers) {
    if (driver.kind !== "driver") continue;
    const others = driver.shipments.filter((s) => s.id !== target.id);
    const sameLocality = locality ? others.filter((s) => searchKey(s.locality) === locality).length : 0;
    const distances = hasCoordinates(target) ? others.filter(hasCoordinates).map((s) => distanceKm(target, s)) : [];
    const nearestKm = distances.length ? Math.min(...distances) : null;
    if (!sameLocality && (nearestKm === null || nearestKm > NEARBY_KM)) continue;
    result.push({ driver, nearestKm, sameLocality, isCurrentDriver: driver.id === target.driverId });
  }
  return result
    .sort(
      (a, b) =>
        (a.nearestKm ?? Infinity) - (b.nearestKm ?? Infinity) ||
        b.sameLocality - a.sameLocality ||
        a.driver.name.localeCompare(b.driver.name, "es"),
    )
    .slice(0, limit);
}

// Envíos sin chofer que siguen abiertos (no entregados ni cancelados).
export function openUnassigned(drivers: TowerDriverDetail[]): TowerShipment[] {
  const unassigned = drivers.find((d) => d.id === "__unassigned__");
  return (
    (unassigned?.shipments || [])
      // Entregados, cancelados y devueltos al cliente ya no se buscan.
      .filter((s) => s.status !== "delivered" && s.status !== "cancelled" && !/DEVUELT/i.test(s.rawStatus))
      .sort((a, b) => (b.operationalDate || "").localeCompare(a.operationalDate || ""))
  );
}

// Envíos Flex en camino con su chofer (panel "En reparto Flex" del Resumen).
// Con `collectedOn`, solo los colectados ese día (base "Fecha de colecta").
export function flexInTransitList(
  drivers: TowerDriverDetail[],
  depot: TowerListedShipment[] = [],
  collectedOn?: string,
): TowerListedShipment[] {
  return listedShipments(drivers, depot)
    .filter(
      (s) =>
        s.status === "inTransit" &&
        s.shippingUniverse === "FLEX" &&
        (!collectedOn || s.operationalDate?.startsWith(collectedOn)),
    )
    .sort((a, b) => a.assignedTo.localeCompare(b.assignedTo, "es") || a.tracking.localeCompare(b.tracking));
}

// Búsqueda por vendedor, dirección, localidad, chofer o tracking.
export function searchListed(list: TowerListedShipment[], query: string): TowerListedShipment[] {
  const q = searchKey(query);
  if (!q) return list;
  return list.filter((s) =>
    searchKey(`${s.client} ${s.address} ${s.locality} ${s.assignedTo} ${s.tracking}`).includes(q),
  );
}
